import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { FormValues } from '../types';
import {
  validateBriefingProducts,
  type BriefingProductValidationItem,
} from '../services/briefingPromocoesApi';

const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;
const DIRECT_PRODUCT_FIELD_KEYS = new Set([
  'eansObrigatorios',
  'skusObrigatorios',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'skusFamilia',
]);
const CONDITIONAL_PRODUCT_FIELD_KEYS: Record<string, string> = {
  itensIncluidos: 'tipoItemIncluido',
  itensExcluidos: 'tipoItemExcluido',
};
const PRODUCT_TYPE_VALUES = new Set(['1', '2']);

export interface ProductValidationState {
  enabled: boolean;
  loading: boolean;
  error: string | null;
  requested: string[];
  products: BriefingProductValidationItem[];
  missing: string[];
  productByCode: Map<string, BriefingProductValidationItem>;
  missingSet: Set<string>;
}

const emptyState: ProductValidationState = {
  enabled: false,
  loading: false,
  error: null,
  requested: [],
  products: [],
  missing: [],
  productByCode: new Map(),
  missingSet: new Set(),
};

export function normalizeProductCode(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

export function parseProductCodes(value: string | string[] | undefined): string[] {
  const rawValues = Array.isArray(value)
    ? value
    : String(value ?? '').split(/[\n,;\t]+/);

  const unique: string[] = [];
  const seen = new Set<string>();

  rawValues.forEach((raw) => {
    const text = String(raw ?? '').trim();
    const normalized = normalizeProductCode(text);
    if (!text || !normalized || seen.has(normalized)) return;

    seen.add(normalized);
    unique.push(text);
  });

  return unique;
}

function baseFieldKey(fieldKey: string): string {
  return fieldKey.replace(CHILD_FIELD_SUFFIX_RE, '');
}

function childSiblingFieldKey(fieldKey: string, siblingBaseKey: string): string {
  const match = fieldKey.match(CHILD_FIELD_SUFFIX_RE);
  return match ? `${siblingBaseKey}__child${match[1]}` : siblingBaseKey;
}

export function shouldValidateProducts(fieldKey: string, values: FormValues): boolean {
  const baseKey = baseFieldKey(fieldKey);

  if (DIRECT_PRODUCT_FIELD_KEYS.has(baseKey)) return true;

  const productTypeField = CONDITIONAL_PRODUCT_FIELD_KEYS[baseKey];
  if (!productTypeField) return false;

  const isChildField = CHILD_FIELD_SUFFIX_RE.test(fieldKey);
  const siblingKey = childSiblingFieldKey(fieldKey, productTypeField);
  const productTypeValue = isChildField ? values[siblingKey] : values[productTypeField];

  return PRODUCT_TYPE_VALUES.has(String(productTypeValue ?? ''));
}

export function useProductValidation(
  value: string | string[] | undefined,
  fieldKey: string,
  values: FormValues
): ProductValidationState {
  const enabled = shouldValidateProducts(fieldKey, values);
  const requested = useMemo(() => (enabled ? parseProductCodes(value) : []), [enabled, value]);
  const requestedKey = requested.map(normalizeProductCode).join('\n');
  const [state, setState] = useState<Omit<ProductValidationState, 'enabled' | 'productByCode' | 'missingSet'>>({
    loading: false,
    error: null,
    requested: [],
    products: [],
    missing: [],
  });

  useEffect(() => {
    if (!enabled || requested.length === 0) {
      setState({
        loading: false,
        error: null,
        requested: [],
        products: [],
        missing: [],
      });
      return;
    }

    let active = true;
    setState((previous) => ({
      ...previous,
      loading: true,
      error: null,
      requested,
    }));

    const timer = window.setTimeout(async () => {
      try {
        const result = await validateBriefingProducts(requested);
        if (!active) return;

        setState({
          loading: false,
          error: null,
          requested: result.requested,
          products: result.found,
          missing: result.missing,
        });
      } catch (error) {
        if (!active) return;

        setState({
          loading: false,
          error: error instanceof Error ? error.message : 'Nao foi possivel validar os produtos.',
          requested,
          products: [],
          missing: [],
        });
      }
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [enabled, requestedKey]);

  const productByCode = useMemo(() => {
    const productsMap = new Map<string, BriefingProductValidationItem>();

    state.products.forEach((product) => {
      const code = normalizeProductCode(product.ean);
      if (code) productsMap.set(code, product);
    });

    return productsMap;
  }, [state.products]);

  const missingSet = useMemo(
    () => new Set(state.missing.map(normalizeProductCode)),
    [state.missing]
  );

  return {
    ...emptyState,
    ...state,
    enabled,
    productByCode,
    missingSet,
  };
}

function formatProductMeta(product: BriefingProductValidationItem): string {
  return [product.nomeMarca, product.nomeIndustria, product.categoria, product.grupo, product.subGrupo]
    .filter(Boolean)
    .join(' | ');
}

export function ProductValidationPreview({ validation }: { validation: ProductValidationState }) {
  if (!validation.enabled || validation.requested.length === 0) return null;

  const visibleProducts = validation.products.slice(0, 6);
  const hiddenProducts = Math.max(validation.products.length - visibleProducts.length, 0);
  const visibleMissing = validation.missing.slice(0, 8);
  const hiddenMissing = Math.max(validation.missing.length - visibleMissing.length, 0);
  const hasMissing = validation.missing.length > 0;

  return (
    <Box
      sx={{
        mt: 1,
        px: 1.25,
        py: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: hasMissing ? 'error.light' : 'primary.light',
        bgcolor: hasMissing ? '#fff7f7' : '#f7fbff',
      }}
    >
      {validation.loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Consultando produtos...
          </Typography>
        </Box>
      )}

      {validation.error && !validation.loading && (
        <Typography variant="caption" color="warning.main" fontWeight={600}>
          Nao foi possivel validar estes SKUs/EANs agora.
        </Typography>
      )}

      {!validation.loading && !validation.error && (
        <>
          <Typography
            variant="caption"
            color={hasMissing ? 'error.main' : 'primary.main'}
            fontWeight={700}
            display="block"
          >
            {validation.products.length > 0
              ? `${validation.products.length} SKU/EAN encontrado(s)`
              : 'Nenhum SKU/EAN encontrado'}
          </Typography>

          {visibleProducts.map((product) => {
            const meta = formatProductMeta(product);

            return (
              <Box key={product.ean} sx={{ mt: 0.5, minWidth: 0 }}>
                <Typography variant="caption" display="block" sx={{ color: 'text.primary' }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {product.ean}
                  </Box>
                  {' - '}
                  {product.nomeProduto || 'Produto sem nome'}
                </Typography>
                {meta && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {meta}
                  </Typography>
                )}
              </Box>
            );
          })}

          {hiddenProducts > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              +{hiddenProducts} produto(s)
            </Typography>
          )}

          {validation.missing.length > 0 && (
            <Typography variant="caption" color="error.main" fontWeight={700} display="block" sx={{ mt: 0.75 }}>
              Nao encontrados: {visibleMissing.join(', ')}
              {hiddenMissing > 0 ? ` +${hiddenMissing}` : ''}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
